import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  psychiatricForms: [],
  currentPsychiatricForm: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch All Psychiatric Forms
export const fetchPsychiatricForms = createAsyncThunk(
  'psychiatricForms/fetchPsychiatricForms',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/psychiatric-form');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Psychiatric forms'
      );
    }
  }
);

// Fetch a single Psychiatric Form by ID
export const fetchPsychiatricForm = createAsyncThunk(
  'psychiatricForms/fetchPsychiatricForm',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/psychiatric-form/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          'Failed to fetch Psychiatric form'
      );
    }
  }
);

// Create a new Psychiatric Form
export const createPsychiatricForm = createAsyncThunk(
  'psychiatricForms/createPsychiatricForm',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post(`/psychiatric-form`, formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create Psychiatric form'
      );
    }
  }
);

// Update Psychiatric Form (ID-based)
export const updatePsychiatricForm = createAsyncThunk(
  'psychiatricForms/updatePsychiatricForm',
  async ({ case_id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/psychiatric-form/case/${case_id}`, formData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update Psychiatric form'
      );
    }
  }
);

// Delete a Psychiatric Form
export const deletePsychiatricForm = createAsyncThunk(
  'psychiatricForms/deletePsychiatricForm',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/psychiatric-form/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          'Failed to delete Psychiatric form'
      );
    }
  }
);

// ==================== Slice ====================
const psychiatricFormSlice = createSlice({
  name: 'psychiatricForms',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentPsychiatricForm: (state, action) => {
      state.currentPsychiatricForm = action.payload;
    },
    clearCurrentPsychiatricForm: (state) => {
      state.currentPsychiatricForm = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // FETCH ALL
      .addCase(fetchPsychiatricForms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPsychiatricForms.fulfilled, (state, action) => {
        state.isLoading = false;
        state.psychiatricForms = action.payload;
      })
      .addCase(fetchPsychiatricForms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchPsychiatricForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPsychiatricForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPsychiatricForm = action.payload;
      })
      .addCase(fetchPsychiatricForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createPsychiatricForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPsychiatricForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.psychiatricForms.unshift(action.payload);
      })
      .addCase(createPsychiatricForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updatePsychiatricForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePsychiatricForm.fulfilled, (state, action) => {
        state.isLoading = false;

        const updated = action.payload.data ?? action.payload;

        const index = state.psychiatricForms.findIndex(
          (item) => item.id === updated.id
        );

        if (index !== -1) {
          state.psychiatricForms[index] = updated;
        }

        if (state.currentPsychiatricForm?.id === updated.id) {
          state.currentPsychiatricForm = updated;
        }
      })
      .addCase(updatePsychiatricForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deletePsychiatricForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePsychiatricForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.psychiatricForms = state.psychiatricForms.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentPsychiatricForm?.id === action.payload) {
          state.currentPsychiatricForm = null;
        }
      })
      .addCase(deletePsychiatricForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentPsychiatricForm,
  clearCurrentPsychiatricForm,
} = psychiatricFormSlice.actions;

export default psychiatricFormSlice.reducer;
